import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { URLS } from "../../Common/axios_instance/axios_instance";
import { getDoctorProfile } from "../../Common/axios_instance/auth_https/auth.https";
import { getConsultationHistory, getIncomingConsultations, completeConsultation } from "../../Common/axios_instance/video_https/video.https";
import { IncomingRequest, DoctorProfile } from "../../Common/interfaces/doctor.interfaces";
import { getMessagingToken, onForegroundMessage } from "../../firebase/messaging";
import { callManager } from "../../Pages/azure/call-manager";

interface DoctorContextType {
  incomingRequest: IncomingRequest | null;
  countdown: number;
  clearIncomingRequest: () => void;
  readNotificationIds: string[];
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: (ids: string[]) => void;
  doctorProfile: DoctorProfile | null;
  isDoctorProfileLoading: boolean;
  todayConsultationCount: number;
  refreshDoctorProfile: () => Promise<void>;
  patientEndedMap: Record<string, boolean>;
}

const DoctorContext = createContext<DoctorContextType>({
  incomingRequest: null,
  countdown: 30,
  clearIncomingRequest: () => {},
  readNotificationIds: [],
  markNotificationAsRead: () => {},
  markAllNotificationsAsRead: () => {},
  doctorProfile: null,
  isDoctorProfileLoading: true,
  todayConsultationCount: 0,
  refreshDoctorProfile: async () => {},
  patientEndedMap: {},
});

export const useDoctorContext = () => useContext(DoctorContext);

export const DoctorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [incomingRequest, setIncomingRequest] = useState<IncomingRequest | null>(null);
  const [countdown, setCountdown] = useState(30);
  const eventSourceRef = useRef<EventSource | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [doctorProfile, setDoctorProfile] = useState<DoctorProfile | null>(null);
  const [isDoctorProfileLoading, setIsDoctorProfileLoading] = useState(true);
  const [todayConsultationCount, setTodayConsultationCount] = useState(0);
  const [patientEndedMap, setPatientEndedMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleEnded = () => {
      const cid = sessionStorage.getItem("activeConsultationId");
      if (cid) {
        completeConsultation(cid).catch(() => {});
        sessionStorage.setItem(`patientEnded:${cid}`, "true");
        setPatientEndedMap((prev) => ({ ...prev, [cid]: true }));
      }
    };
    callManager.addOnCallEnded(handleEnded);
    return () => {
      callManager.removeOnCallEnded(handleEnded);
    };
  }, []);

  const refreshDoctorProfile = async () => {
    try {
      const r = await getDoctorProfile();
      setDoctorProfile(r.data?.data || null);
    } catch {
      setDoctorProfile(null);
    }
  };

  useEffect(() => {
    getDoctorProfile()
      .then((r) => setDoctorProfile(r.data?.data || null))
      .catch(() => setDoctorProfile(null))
      .finally(() => setIsDoctorProfileLoading(false));
  }, []);

  useEffect(() => {
    getConsultationHistory({ page: 1, limit: 20 })
      .then((res) => {
        const items: any[] = res.data?.data || [];
        const today = new Date().toDateString();
        setTodayConsultationCount(items.filter((item) => new Date(item.requested_at).toDateString() === today).length);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getIncomingConsultations()
      .then((res) => {
        const data = res.data?.data || [];
        if (data.length > 0) {
          const cons = data[0].consultation;
          if (cons && cons.status === "requested") {
            setIncomingRequest({
              consultation_id: cons._id,
              ticket_id: cons.ticket_id ? cons.ticket_id.toString() : "",
              patient_name: cons.patient_name || "Patient",
              patient_age: cons.patient_age || 0,
              patient_gender: cons.patient_gender || "",
              consultation_mode: cons.consultation_mode || "video",
            });
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setupSSE(); // eslint-disable-line react-hooks/exhaustive-deps
    return () => {
      cleanupSSE();
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FCM push notification setup — runs once after login
  useEffect(() => {
    const setupFCM = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const token = await getMessagingToken();
        if (!token) return;

        // Register token with auth service
        await axios.post(`${URLS.AUTH_URI}/fcm-tokens/upsert`, {
          token,
          platform: 'doctor',
          platformType: 'web',
        }, { withCredentials: true });

        console.log('[FCM] Doctor FCM token registered');
      } catch (err) {
        console.warn('[FCM] Failed to register doctor FCM token:', err);
      }
    };

    setupFCM();

    // Foreground message handler
    const unsubForeground = onForegroundMessage((payload) => {
      const data = payload.data || {};
      if (data.type === 'incoming_consultation' || data.consultationId) {
        setIncomingRequest({
          consultation_id: data.consultationId,
          patient_name: data.patientName || 'Patient',
          consultation_mode: data.consultationMode || 'video',
          ticket_id: data.ticketId,
        } as any);
      }
    });

    // Background notification forwarded from service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'BACKGROUND_NOTIFICATION') {
        const data = event.data.payload?.data || {};
        if (data.consultationId) {
          setIncomingRequest({
            consultation_id: data.consultationId,
            patient_name: data.patientName || 'Patient',
            consultation_mode: data.consultationMode || 'video',
            ticket_id: data.ticketId,
          } as any);
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      unsubForeground();
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!incomingRequest) return;
    setCountdown(30);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(countdownRef.current!); setIncomingRequest(null); return 30; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [incomingRequest]);

  const setupSSE = () => {
    cleanupSSE();
    const es = new EventSource(`${URLS.VIDEO_URI}/doctor-sse/register`, { withCredentials: true });
    eventSourceRef.current = es;
    es.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.type === "incoming_request") setIncomingRequest(parsed.payload || parsed);
      } catch (err) { console.error("SSE parse error", err); }
    };
    es.onerror = () => {
      console.warn("SSE connection lost. Reconnecting in 5s...");
      cleanupSSE();
      reconnectTimeoutRef.current = setTimeout(() => setupSSE(), 5000);
    };
  };

  const cleanupSSE = () => { eventSourceRef.current?.close(); eventSourceRef.current = null; };

  const clearIncomingRequest = () => {
    setIncomingRequest(null);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  const [readNotificationIds, setReadNotificationIds] = useState<string[]>(() => {
    try { const saved = localStorage.getItem("read_notification_ids"); return saved ? JSON.parse(saved) : []; }
    catch { return []; }
  });

  const markNotificationAsRead = (id: string) => {
    setReadNotificationIds((prev) => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      localStorage.setItem("read_notification_ids", JSON.stringify(updated));
      return updated;
    });
  };

  const markAllNotificationsAsRead = (ids: string[]) => {
    setReadNotificationIds((prev) => {
      const newIds = ids.filter((id) => !prev.includes(id));
      if (newIds.length === 0) return prev;
      const updated = [...prev, ...newIds];
      localStorage.setItem("read_notification_ids", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <DoctorContext.Provider value={{ incomingRequest, countdown, clearIncomingRequest, readNotificationIds, markNotificationAsRead, markAllNotificationsAsRead, doctorProfile, isDoctorProfileLoading, todayConsultationCount, refreshDoctorProfile, patientEndedMap }}>
      {children}
    </DoctorContext.Provider>
  );
};
